<template>
  <div
    :class="{
      'flex flex-col gap-2': !showHorizontal,
      'grid grid-cols-[1fr_2fr] gap-6 items-center':
        showHorizontal && !isMobile,
      'flex justify-between gap-6 items-center': showHorizontal && isMobile,
    }"
    v-if="field.show ? field.show(field, form) : true"
    :key="field.key"
  >
    <div class="grow" v-if="showTitle">
      <span v-if="field.title" class="font-medium text-sm">
        {{ field.title }}
        <span v-if="field.required" class="-ml-[4px]">*</span>
      </span>
      <p
        v-if="field.description && !compact"
        class="text-gray-500 text-sm font-medium"
      >
        {{ field.description }}
      </p>
    </div>
    <div
      :class="[
        showDeleteIcon ? 'flex items-center gap-3' : '',
        limitWidth ? 'max-w-[400px]' : '',
      ]"
    >
      <DropdownSelector
        v-if="field.type === 'dropdown'"
        :value="modelValue"
        @change="(e) => onChange(field, e)"
        :placeholder="field.placeholder"
        :type="field.type"
        :name="field.key"
        :id="field.key"
        :options="toValue(field.options)"
        selectorStyle="input"
        :disabled="disabled"
        :preFilled="field.preFilled"
        class="block w-full rounded-sm border-gray-300 shadow-sm focus:border-gray-500 focus:ring-gray-500 sm:text-sm"
        v-bind="extraAttributes(field)"
      />
      <UserSelector
        v-else-if="field.type === 'users'"
        :modelValue="modelValue ?? []"
        @change="(e) => emitUpdate(field, e)"
        :emptyLabel="field.placeholder"
        :disabled="disabled"
        class="block w-full rounded-sm border-gray-300 shadow-sm focus:border-gray-500 focus:ring-gray-500 sm:text-sm"
        v-bind="extraAttributes(field)"
      />
      <HierarchySelector
        v-else-if="field.type === 'space'"
        :hierarchy="spaceHierarchy"
        :label="t('admin.search.filters.space')"
        :search-placeholder="t('components.inputs.searchLocations')"
        :value="modelValue"
        @change="(value) => onChange(field, value)"
        :placeholder="field.placeholder"
        :type="field.type"
        :name="field.key"
        :id="field.key"
        :disabled="disabled"
        class="block w-full rounded-sm border-gray-300 shadow-sm focus:border-gray-500 focus:ring-gray-500 sm:text-sm"
        v-bind="extraAttributes(field)"
      />
      <HierarchySelector
        v-else-if="field.type === 'hierarchy'"
        allowTenantSelection
        :hierarchy="field.options"
        :search-placeholder="t('components.inputs.search')"
        :value="modelValue"
        @change="(e) => onChange(field, e.id)"
        :name="field.key"
        :id="field.key"
        :disabled="disabled"
        selectorStyle="dropdown"
        class="block w-full rounded-sm border-gray-300 shadow-sm focus:border-gray-500 focus:ring-gray-500 sm:text-sm"
        v-bind="extraAttributes(field)"
      />
      <CompositeField
        v-else-if="field.type === 'composite'"
        :fields="field.fields"
        :form="form"
        :disabled="disabled"
        :debounce="debounce"
        @compositeInput="onChange"
      />
      <AvatarInput
        v-else-if="field.type === 'avatar'"
        :disabled="disabled"
        @file="onChange(field, $event)"
        v-bind="extraAttributes(field)"
      />
      <AvatarInput
        v-else-if="field.type === 'locationImage'"
        :disabled="disabled"
        @file="onChange(field, $event)"
        v-bind="extraAttributes(field)"
      />
      <LocationImageInput
        v-else-if="field.type === 'image'"
        :disabled="disabled"
        @file="onChange(field, $event)"
        :value="field.value ?? form[field.key]"
        :placeholder="field.placeholder"
      />
      <TaskMediaSelector
        v-else-if="field.type === 'taskMedia'"
        :disabled="disabled"
        :value="field.value ?? form[field.key]"
        @files="onChange(field, $event)"
      />
      <DocumentsSelector
        v-else-if="field.type === 'documents'"
        :disabled="disabled"
        :value="field.value ?? form[field.key]"
        @files="onChange(field, $event)"
      />
      <SwitchInput
        v-else-if="field.type === 'switch'"
        :disabled="disabled"
        :label="field.title"
        :description="field.description"
        :form="form"
        :debounce="debounce"
        :dependentFields="field.fields"
        @update:modelValue="onChange"
        @switchInput="onChange(field, $event)"
        :modelValue="field.value ?? form[field.key]"
        v-bind="extraAttributes(field)"
      />
      <DateInput
        v-else-if="field.type === 'date'"
        inputStyle="input"
        :modelValue="modelValue"
        placement="bottom"
        :disabled="disabled"
        @update:modelValue="onChange(field, $event)"
        v-bind="extraAttributes(field)"
      />
      <LocationDropdown
        v-else-if="field.type === 'location'"
        :modelValue="modelValue"
        @update:modelValue="emitUpdate(field, $event)"
        :disabled="disabled"
        v-bind="extraAttributes(field)"
      />
      <TimeRange
        v-else-if="field.type === 'timeRange'"
        :modelValue="modelValue"
        @update:modelValue="emitUpdate(field, $event)"
        :disabled="disabled"
        v-bind="extraAttributes(field)"
      />
      <CostInput
        v-else-if="field.type === 'cost'"
        :value="modelValue"
        @input="(e) => onInput(field, e)"
        :placeholder="field.placeholder"
        :inputType="field.type"
        :name="field.key"
        :id="field.key"
        :options="toValue(field.options)"
        :disabled="disabled"
        :compact="compact"
        :description="field.description"
        v-bind="extraAttributes(field)"
        :autofocus="autofocus"
      />
      <textarea
        v-else-if="field.type === 'textarea'"
        :value="modelValue"
        @input="(e) => onInput(field, e)"
        :placeholder="field.placeholder"
        :type="field.type"
        :name="field.key"
        :id="field.key"
        :options="toValue(field.options)"
        :disabled="disabled"
        rows="5"
        class="block w-full rounded-sm border-gray-300 focus:border-gray-500 focus:ring-gray-500 sm:text-sm"
        :class="{
          'shadow-sm': !field.component,
          'opacity-60 !cursor-not-allowed select-none': disabled,
        }"
        v-bind="extraAttributes(field)"
        :autofocus="autofocus"
      />
      <component
        v-else-if="field.component"
        :is="field.component"
        :value="modelValue"
        @change="(e) => onChange(field, e)"
        :placeholder="field.placeholder"
        :type="field.type"
        :name="field.key"
        :id="field.key"
        :options="toValue(field.options)"
        :disabled="disabled"
        rows="5"
        class="block w-full rounded-sm border-gray-300 focus:border-gray-500 focus:ring-gray-500 sm:text-sm"
        v-bind="extraAttributes(field)"
      />
      <TextInput
        v-else
        :value="modelValue"
        @input="(e) => onInput(field, e)"
        :placeholder="field.placeholder"
        :inputType="field.type"
        :name="field.key"
        :id="field.key"
        :options="toValue(field.options)"
        :disabled="disabled"
        :compact="compact"
        :description="field.description"
        v-bind="extraAttributes(field)"
        :autofocus="autofocus"
      />

      <div v-if="showDeleteIcon" @click="handleDelete(field)">
        <TrashOne
          class="transition cursor-pointer text-gray-400 hover:text-red-600 w-4 h-4"
        />
      </div>

      <p class="text-[0.65rem] pt-1 text-gray-600" v-if="field.inputHint">
        {{
          typeof field.inputHint === "function"
            ? field.inputHint(field, form)
            : field.inputHint
        }}
      </p>
    </div>
  </div>
</template>

<script setup>
import DropdownSelector from "@/Tailwind/Components/Input/DropdownSelector.vue";
import LocationDropdown from "@/Tailwind/Components/Input/LocationDropdown.vue";
import SwitchInput from "@/Tailwind/Components/Input/SwitchInput.vue";
import DateInput from "@/Tailwind/Components/Input/DateInput.vue";
import CompositeField from "@/Tailwind/Components/Settings/CompositeField.vue";
import AvatarInput from "@/Tailwind/Components/Settings/Users/AvatarInput.vue";
import LocationImageInput from "@/Tailwind/Components/Settings/LocationSettings/LocationImageInput.vue";
import { useBreakpoints } from "@/Tailwind/Mixins/useBreakpoints";

import { toValue, useDebounceFn } from "@vueuse/core";
import { computed } from "vue";
import HierarchySelector from "@/Tailwind/Components/Input/HierarchySelector.vue";
import TextInput from "@/Tailwind/Components/Input/TextInput.vue";
import TaskMediaSelector from "@/Tailwind/Components/Input/TaskMediaSelector.vue";
import UserSelector from "@/Tailwind/Components/Input/UserSelector.vue";
import { useI18n } from "vue-i18n";
import TimeRange from "@/Tailwind/Components/Input/TimeRange.vue";
import CostInput from "@/Tailwind/Components/Input/CurrencyInput.vue";
import DocumentsSelector from "@/Tailwind/Components/Input/DocumentsSelector.vue";
import { usePreloadStore } from "@/Tailwind/Stores/preload";
import TrashOne from "@/Tailwind/Icon/TrashOne.vue";

const props = defineProps({
  field: Object,
  modelValue: [String, Number, Boolean, Object, Array],
  disabled: Boolean,
  form: Object,
  horizontal: Boolean,
  debounce: {
    type: Boolean,
    default: false,
  },
  simpleUpdate: {
    type: Boolean,
    default: false,
  },
  compact: Boolean,
  autofocus: Boolean,
  limitWidth: {
    type: Boolean,
    default: true,
  },
  showDeleteIcon: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();

const preloadStore = usePreloadStore();
const spaceHierarchy = computed(() => preloadStore.locations);

const emit = defineEmits(["update:modelValue", "delete"]);

const { isMobile } = useBreakpoints();

const showTitle = computed(() => {
  return (
    (props.field.title || props.field.description) &&
    props.field.type !== "switch"
  );
});

const showHorizontal = computed(() => {
  return props.horizontal;
});

const showDeleteIcon = computed(() => {
  return props.showDeleteIcon;
});

const onInput = useDebounceFn(
  (field, e) => {
    onInputWithoutDebounce(field, e);
  },
  props.debounce ? 750 : 0
);

const onInputWithoutDebounce = (field, e) => {
  // Custom components return the value directly instead of an event
  const value = field.component ? e : e.target.value;

  emitUpdate(field, value);
};

const onChange = useDebounceFn(
  (field, e) => {
    emitUpdate(field, e);
  },
  props.debounce ? 750 : 0
);

const emitUpdate = (field, value) => {
  if (props.simpleUpdate) {
    emit("update:modelValue", value);

    return;
  }

  emit("update:modelValue", field, value);
};

const handleDelete = (field) => {
  emit("delete", field);
};

const extraAttributes = (field) => {
  const extraData = { ...(field.extraAttributes ?? {}) };

  if (!field.injectFormData) {
    return extraData;
  }

  for (const [fieldKey, callback] of Object.entries(field.injectFormData)) {
    extraData[fieldKey] = callback(props.form);
  }

  return extraData;
};
</script>
